import { useEffect, useMemo, useRef } from 'react';

import { SelectorLocation } from '@candu-ai/placement/types';
import {
  addPreviewElementToSnapshot,
  updatePreviewElement,
} from 'craft/containers/Toolbox/tabs/Placements/components/InteractivePlacementPicker';
import { useCurrentSnapshot, usePlacementMode, useToolboxTab } from 'craft/state';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

interface UseSyncPreviewHighlight {
  iframeRef?: HTMLIFrameElement;
  syncIterator?: number;
}

export const useSyncPreviewHighlight = ({ iframeRef, syncIterator }: UseSyncPreviewHighlight) => {
  const { placement, isSnapshotLoaded } = useCurrentSnapshot();
  const { forcePlacementsTab } = useToolboxTab();
  const { placementMode, previewSelector, previewSelectorLocation } = usePlacementMode();

  const injectedPreviewElement = useRef<HTMLDivElement>();
  const activeSelector = previewSelector || placement?.selector;
  const activeLocation = previewSelectorLocation || placement?.location;

  const snapshotDocument = useMemo(() => iframeRef?.contentDocument, [
    iframeRef?.contentDocument,
  ]) as Document;

  useEffect(() => {
    if (!syncIterator) {
      return;
    }

    // when we are actively editing an Inline placement and are not in placement mode,
    // highlight the target element in snapshot
    if (
      forcePlacementsTab &&
      !placementMode &&
      isSnapshotLoaded &&
      activeLocation !== SelectorLocation.REPLACE
    ) {
      const target = getSelectorFromIframe(activeSelector) as HTMLElement | null;

      if (target) {
        const previewElement =
          injectedPreviewElement.current || addPreviewElementToSnapshot(snapshotDocument);
        injectedPreviewElement.current = previewElement;
        updatePreviewElement(previewElement, target, snapshotDocument);
      }
    } else {
      injectedPreviewElement.current?.remove();
      injectedPreviewElement.current = undefined;
    }
  }, [
    forcePlacementsTab,
    placementMode,
    snapshotDocument,
    activeSelector,
    activeLocation,
    isSnapshotLoaded,
    syncIterator,
  ]);
};
